import { useEffect, useState } from "react";
import { ArrowsClockwise, DownloadSimple } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { API_BASE, formatApiErrorDetail } from "@/lib/api";

const LABELS = {
  PPL: "Özel Pilot", CPL: "Ticari Pilot", ATPL: "Havayolu Pilotu",
  CABIN_CREW: "Kabin Görevlisi", CABIN_SENIOR: "Kıdemli Kabin / Purser", CABIN_SAFETY: "Kabin Güvenlik",
  SAFETY_OFFICER: "Güvenlik Yetkilisi", EMERGENCY_RESPONSE: "Acil Müdahale", FIRE_SAFETY: "Yangın Güvenliği",
  FIRST_AID: "İlk Yardım", BLS: "Temel Yaşam Desteği", AVIATION_MEDICAL: "Havacılık Tıp",
  GROUND_STAFF: "Yer Hizmetleri", RAMP_AGENT: "Apron / Ramp", LOAD_CONTROL: "Yük Kontrol",
  DG_HANDLING: "Tehlikeli Madde", PASSENGER_SERVICE: "Yolcu Hizmetleri",
};

export default function RevokedList() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      setLoading(true);
      const { data } = await api.get("/certificates", { params: { status_filter: "REVOKED" } });
      setItems(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Yüklenemedi.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const onReinstate = async (id) => {
    try {
      await api.post(`/certificates/${id}/reinstate`);
      toast.success("Sertifika yeniden etkinleştirildi.");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "İşlem başarısız.");
    }
  };

  return (
    <div className="p-6 lg:p-10" data-testid="revoked-page">
      <span className="label-eyebrow">/ İptal Edilen Sertifikalar</span>
      <h1 className="font-display text-4xl lg:text-5xl font-black tracking-tighter text-slate-900 mt-3">
        İptal kaydı
      </h1>
      <p className="text-slate-600 mt-2 max-w-2xl">
        Yetki tarafından iptal edilmiş sertifikalar. Halka açık sorgu açık biçimde
        &quot;İptal Edildi&quot; yanıtı verir. &quot;Aktifleştir&quot; ile sertifikayı yeniden geçerli hale getirebilirsiniz.
      </p>

      <div className="mt-8 flex justify-end">
        <button
          onClick={load}
          className="px-3 py-2 border border-slate-300 text-slate-700 hover:bg-slate-50 inline-flex items-center gap-2 text-sm"
        >
          <ArrowsClockwise size={14} /> Yenile
        </button>
      </div>

      <div className="mt-4 border border-slate-200 bg-white overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-500">
            <tr>
              <Th>Sertifika №</Th>
              <Th>Sertifika Sahibi</Th>
              <Th>Lisans Türü</Th>
              <Th>Uyruk</Th>
              <Th>İlk Düzenleme</Th>
              <Th>Geçerlilik</Th>
              <Th className="text-right">İşlem</Th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={7} className="px-4 py-8 text-center label-eyebrow">Yükleniyor…</td></tr>
            )}
            {!loading && items.length === 0 && (
              <tr><td colSpan={7} className="px-4 py-12 text-center label-eyebrow">İptal edilmiş sertifika yok</td></tr>
            )}
            {items.map((c) => (
              <tr key={c.id} className="border-t border-slate-200 hover:bg-slate-50">
                <Td mono>{c.certificate_number}</Td>
                <Td>{c.holder_name}</Td>
                <Td>{LABELS[c.license_type] || c.license_type}</Td>
                <Td>{c.nationality}</Td>
                <Td mono>{c.issue_date}</Td>
                <Td mono>{c.expiry_date}</Td>
                <Td className="text-right whitespace-nowrap">
                  <a
                    href={`${API_BASE}/certificates/${c.certificate_number}/pdf`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 px-2 py-1 text-xs text-slate-700 hover:bg-slate-100"
                  >
                    <DownloadSimple size={14} /> PDF
                  </a>
                  <button
                    onClick={() => onReinstate(c.id)}
                    data-testid={`reinstate-${c.id}`}
                    className="ml-1 inline-flex items-center gap-1 px-2 py-1 text-xs text-emerald-700 hover:bg-emerald-50"
                  >
                    <ArrowsClockwise size={14} /> Aktifleştir
                  </button>
                </Td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Th({ children, className = "" }) {
  return (
    <th className={`text-left font-mono-plex font-medium text-[11px] uppercase tracking-wider px-4 py-3 ${className}`}>
      {children}
    </th>
  );
}
function Td({ children, mono, className = "" }) {
  return (
    <td className={`px-4 py-3 ${mono ? "font-mono-plex" : ""} text-slate-800 ${className}`}>
      {children}
    </td>
  );
}
